import React from 'react';
import SomethingWentWrong from "../../../layout/default/something-went-wrong/SomethingWentWrong";
import * as Sentry from '@sentry/react';

interface ErrorBoundaryState {
    hasError: boolean;
    errorMessage?: string;
    recoverKey: number;
    recoverAttempts: number;
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

const MAX_RECOVERABLE_DOM_MUTATION_RECOVERIES = 3;

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {hasError: false, recoverKey: 0, recoverAttempts: 0};
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return {hasError: true, errorMessage: error.message};
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        if (this.isRecoverableDomMutationError(error)
            && this.state.recoverAttempts < MAX_RECOVERABLE_DOM_MUTATION_RECOVERIES) {
            this.setState((prev) => ({
                hasError: false,
                errorMessage: undefined,
                recoverKey: prev.recoverKey + 1,
                recoverAttempts: prev.recoverAttempts + 1
            }));
            return;
        }
        Sentry.captureException(error);
        console.error('Error caught by Error Boundary: ', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return <SomethingWentWrong/>
        }

        return <React.Fragment key={this.state.recoverKey}>{this.props.children}</React.Fragment>;
    }

    private isRecoverableDomMutationError(error: Error): boolean {
        if ((error as any)?.name !== "NotFoundError") {
            return false;
        }
        const message = error.message || "";
        return message.includes("insertBefore")
            || message.includes("removeChild")
            || message.includes("replaceChild");
    }
}

export default ErrorBoundary;
